import { accentDe, ENCRE, texteSurAccent, type MarqueDeDocument } from './branding-rendu.js';
import { echapper } from './echapper.js';

/**
 * LES BLOCS COURANTS D'UN DOCUMENT. Chacun recoit du TEXTE (echappe ici) et
 * rend du HTML ; le corps d'un document est une suite de ces blocs, joints.
 * Une valeur `null` ou vide s'ecrit « Non renseigné », comme sur la fiche.
 */

export type Valeur = string | number | null | undefined;

export const NON_RENSEIGNE = 'Non renseigné';

function texte(valeur: Valeur): string {
  if (valeur === null || valeur === undefined || valeur === '') return `<span style="color:${ENCRE.doux}">${NON_RENSEIGNE}</span>`;
  return echapper(String(valeur)).replace(/\r?\n/g, '<br>');
}

/** Un titre de section. */
export function titre(libelle: string): string {
  return `<h2 style="margin:24px 0 8px 0;font-size:14px;font-weight:700;color:${ENCRE.texte}">${echapper(libelle)}</h2>`;
}

/** Un paragraphe ; les retours a la ligne sont gardes. */
export function paragraphe(contenu: string): string {
  return `<p style="margin:0 0 10px 0">${texte(contenu)}</p>`;
}

/**
 * UN BLOC DE CHAMPS : libelle a gauche, valeur a droite, une ligne par champ.
 * La forme d'une fiche imprimee.
 */
export function bloc(intitule: string | null, lignes: ReadonlyArray<readonly [string, Valeur]>): string {
  const rangs = lignes
    .map(([libelle, valeur]) =>
      `<tr><th scope="row" style="width:34%;padding:6px 12px 6px 0;text-align:left;vertical-align:top;font-weight:500;color:${ENCRE.doux};border-bottom:1px solid ${ENCRE.filet}">${echapper(libelle)}</th>` +
      `<td style="padding:6px 0;vertical-align:top;border-bottom:1px solid ${ENCRE.filet}">${texte(valeur)}</td></tr>`)
    .join('');
  return `<section class="bloc">${intitule ? titre(intitule) : ''}<table width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse">${rangs}</table></section>`;
}

export interface Colonne {
  libelle: string;
  /** `droite` pour les nombres et les montants. */
  alignement?: 'gauche' | 'droite';
}

/**
 * UN TABLEAU : en-tete a la couleur d'accent (texte lisible dessus), repete en
 * haut de chaque page quand il deborde, lignes jamais coupees en deux.
 */
export function tableau(marque: MarqueDeDocument, colonnes: readonly Colonne[], lignes: ReadonlyArray<readonly Valeur[]>, pied?: readonly Valeur[]): string {
  const accent = accentDe(marque);
  const sur = texteSurAccent(accent);
  const aligne = (i: number) => (colonnes[i]?.alignement === 'droite' ? 'right' : 'left');
  const entete = colonnes
    .map((colonne, i) => `<th style="padding:6px 8px;text-align:${aligne(i)};background:${accent};color:${sur};font-weight:600">${echapper(colonne.libelle)}</th>`)
    .join('');
  const corps = lignes
    .map((ligne, r) => `<tr style="${r % 2 ? `background:${ENCRE.fond}` : ''}">${colonnes.map((_, i) => `<td style="padding:6px 8px;text-align:${aligne(i)};border-bottom:1px solid ${ENCRE.filet}">${texte(ligne[i])}</td>`).join('')}</tr>`)
    .join('');
  const total = pied
    ? `<tfoot><tr>${colonnes.map((_, i) => `<td style="padding:6px 8px;text-align:${aligne(i)};font-weight:700;border-top:2px solid ${ENCRE.texte}">${pied[i] === undefined || pied[i] === null ? '' : echapper(String(pied[i]))}</td>`).join('')}</tr></tfoot>`
    : '';
  return `<table class="tableau" width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;margin:8px 0 16px 0"><thead><tr>${entete}</tr></thead><tbody>${corps}</tbody>${total}</table>`;
}

/** Force la suite du document sur une nouvelle page. */
export function sautDePage(): string {
  return '<div class="saut-de-page"></div>';
}
